import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControlLabel,
  Switch,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";

import DatosBasicosApi from "api/datosbasicosApi";
import { obtenerMensajeError } from "utils/apiErrors";
import { invalidarParametrosGenerales } from "hooks/useParametrosGenerales";

const FORM_INICIAL = {
  nombre: "",
  descripcion: "",
  valor: "",
  estado: true,
};

const EditParametroModal = ({ open, onClose, parametro, onSuccess }) => {
  const [error, setError] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(FORM_INICIAL);

  useEffect(() => {
    if (parametro) {
      setFormData({
        nombre: parametro.nombre,
        descripcion: parametro.descripcion || "",
        valor: parametro.valor,
        estado: parametro.estado,
      });
    } else {
      setFormData(FORM_INICIAL);
    }
    setError(undefined);
  }, [parametro, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      // El nombre se usa como llave en el código: mayúsculas y sin espacios
      [name]: name === "nombre" ? value.toUpperCase().replace(/\s/g, "_") : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      setError("Debes registrar el Nombre del parámetro.");
      return;
    }
    if (!formData.valor.trim()) {
      setError("Debes registrar el Valor del parámetro.");
      return;
    }
    if (parametro) {
      const confirmacion = window.confirm(
        `¿Está seguro(a) de actualizar el parámetro ${parametro.nombre}? El cambio aplica de inmediato en toda la plataforma.`
      );
      if (!confirmacion) return;
    }
    try {
      setSaving(true);
      if (parametro) {
        await DatosBasicosApi.updateConfigParametro(parametro.id, formData);
      } else {
        await DatosBasicosApi.createConfigParametro(formData);
      }
      invalidarParametrosGenerales();
      onSuccess();
    } catch (error) {
      console.error("Error al guardar el parámetro:", error);
      setError(obtenerMensajeError(error, "Error al guardar el Parámetro."));
    } finally {
      setSaving(false);
    }
  };

  const hCClose = () => {
    setError(undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={hCClose} maxWidth="md" fullWidth>
      <DialogTitle>{parametro ? "Editar Parámetro" : "Nuevo Parámetro"}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Nombre
              </SuiTypography>
              <SuiInput
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="EJ: NOMBRE_PARAMETRO"
                disabled={Boolean(parametro)}
                readOnly={Boolean(parametro)}
                inputProps={{ maxLength: 50 }}
                fullWidth
              />
            </Grid>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Estado
              </SuiTypography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.estado}
                    onChange={(e) => setFormData((prev) => ({ ...prev, estado: e.target.checked }))}
                  />
                }
                label={formData.estado ? "Activo" : "Inactivo"}
              />
            </Grid>
            <Grid size={12}>
              <SuiTypography variant="h6" fontWeight="light">
                Valor
              </SuiTypography>
              <SuiInput
                name="valor"
                value={formData.valor}
                onChange={handleChange}
                placeholder="Valor del parámetro"
                inputProps={{ maxLength: 500 }}
                fullWidth
              />
              {parametro?.formato && (
                <SuiTypography variant="caption" color="text">
                  Formato: {parametro.formato}
                </SuiTypography>
              )}
            </Grid>
            <Grid size={12}>
              <SuiTypography variant="h6" fontWeight="light">
                Descripción
              </SuiTypography>
              <SuiInput
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                placeholder="Breve descripción del uso del parámetro"
                multiline
                rows={3}
                inputProps={{ maxLength: 1000 }}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={hCClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton type="submit" variant="gradient" buttonColor="dark" disabled={saving}>
            {saving ? "Guardando..." : parametro ? "Actualizar" : "Crear"}
          </SuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

EditParametroModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  parametro: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default EditParametroModal;
